import { useTranslation } from "react-i18next";
import { PageSEO } from "@/components/seo/PageSEO";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Lightbulb, HeartHandshake, ShieldCheck, Users, Building2, Handshake, Award, Leaf, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const OmGeposit = () => {
  const { t } = useTranslation();

  return (
    <div>
      <PageSEO
        title={t("nav.omGeposit")}
        description={t("seo.omGeposit")}
        structuredData={{
          "@type": "Organization",
          name: "Geposit",
          url: "https://geposit.se",
          description: t("seo.omGeposit"),
        }}
      />
      {/* Hero */}
      <section className="bg-primary text-primary-foreground py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t("about.hero.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              {t("about.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight mb-8">
                {t("about.story.title")}
              </h2>
              <div className="space-y-5 text-muted-foreground font-body leading-relaxed text-lg">
                <p>{t("about.story.p1")}</p>
                <p>{t("about.story.p2")}</p>
                <p>{t("about.story.p3")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("about.values.title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-4xl mx-auto">
            {[
              { key: "innovation", icon: Lightbulb },
              { key: "trust", icon: HeartHandshake },
              { key: "quality", icon: ShieldCheck },
            ].map((value, index) => (
              <motion.div
                key={value.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-petrol-light flex items-center justify-center mx-auto mb-5">
                  <value.icon className="h-6 w-6 text-primary dark:text-white" />
                </div>
                <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                  {t(`about.values.${value.key}.title`)}
                </h3>
                <p className="text-muted-foreground font-body leading-relaxed">
                  {t(`about.values.${value.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Progrits + Postnummerservice */}
      <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Progrits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-primary-foreground/5 rounded-2xl p-8 lg:p-10 border border-primary-foreground/10"
            >
              <div className="w-12 h-12 rounded-lg bg-coral/20 flex items-center justify-center mb-5">
                <Building2 className="h-6 w-6 text-coral" />
              </div>
              <h2 className="text-2xl md:text-3xl font-medium font-heading mb-2">
                {t("about.progrits.title")}
              </h2>
              <p className="text-sm font-bold font-subheading text-coral uppercase tracking-widest mb-4">
                {t("about.progrits.tagline")}
              </p>
              <div className="space-y-4 text-primary-foreground/70 font-body leading-relaxed">
                <p>{t("about.progrits.description")}</p>
                <p>{t("about.progrits.description2")}</p>
              </div>
              <a
                href="https://www.progrits.se"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mt-6 text-sm font-bold font-subheading text-coral hover:text-coral/80 transition-colors"
              >
                {t("about.progrits.link")}
                <ExternalLink className="ml-1.5 h-4 w-4" />
              </a>
            </motion.div>

            {/* Postnummerservice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-primary-foreground/5 rounded-2xl p-8 lg:p-10 border border-primary-foreground/10"
            >
              <div className="w-12 h-12 rounded-lg bg-coral/20 flex items-center justify-center mb-5">
                <Handshake className="h-6 w-6 text-coral" />
              </div>
              <h2 className="text-2xl md:text-3xl font-medium font-heading mb-4">
                {t("about.postnummerservice.title")}
              </h2>
              <div className="space-y-4 text-primary-foreground/70 font-body leading-relaxed">
                <p>{t("about.postnummerservice.description")}</p>
                <p>{t("about.postnummerservice.description2")}</p>
              </div>
              <a
                href="https://www.postnummerservice.se"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mt-6 text-sm font-bold font-subheading text-coral hover:text-coral/80 transition-colors"
              >
                {t("about.postnummerservice.link")}
                <ExternalLink className="ml-1.5 h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ISO Certifications */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("about.certifications.title")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body leading-relaxed">
              {t("about.certifications.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { key: "iso9001", icon: Award },
              { key: "iso14001", icon: Leaf },
              { key: "iso27001", icon: Lock },
            ].map((cert, index) => (
              <motion.div
                key={cert.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card rounded-2xl border border-border p-6 text-center shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-petrol-light flex items-center justify-center mx-auto mb-4">
                  <cert.icon className="h-5 w-5 text-primary dark:text-white" />
                </div>
                <h3 className="text-lg font-bold font-subheading text-foreground">
                  {t(`about.certifications.${cert.key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground font-body mt-1">
                  {t(`about.certifications.${cert.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <div className="w-14 h-14 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-6">
              <Users className="h-7 w-7 text-coral" />
            </div>
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("about.careers.title")}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body leading-relaxed">
              {t("about.careers.description")}
            </p>
            <Button
              size="lg"
              asChild
              className="mt-8 bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
            >
              <a href="https://career.geposit.se/" target="_blank" rel="noopener noreferrer">
                {t("about.careers.cta")}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OmGeposit;
